import { FocusMonitor } from '@angular/cdk/a11y'
import { COMMA, ENTER } from '@angular/cdk/keycodes'
import { CommonModule } from '@angular/common'
import { ChangeDetectorRef, Component, ElementRef, Input, Optional, Self } from '@angular/core'
import { FormGroupDirective, NgControl, NgForm, type ValidatorFn } from '@angular/forms'
import { MatButtonModule } from '@angular/material/button'
import { MatChipInputEvent, MatChipsModule } from '@angular/material/chips'
import { MatFormFieldControl } from '@angular/material/form-field'
import { MatIconModule } from '@angular/material/icon'
import { isValidCPT } from '@mmx/shared'
import { TranslateModule } from '@ngx-translate/core'

import { CustomMaterialInputControlComponent } from '../custom-field'

@Component({
  selector: 'mmx-codes-selector',
  templateUrl: './codes-selector.component.html',
  providers: [
    {
      provide: MatFormFieldControl,
      useExisting: CodesSelectorInputComponent,
    },
  ],
  standalone: true,
  imports: [
    CommonModule,
    MatButtonModule,
    MatChipsModule,
    MatIconModule,
    TranslateModule,
  ],
})
export class CodesSelectorInputComponent extends CustomMaterialInputControlComponent<string[]> {
  /** Name of the error this control sets while a paste still has refused codes in the input. */
  public static readonly INVALID_CODES_ERROR = 'invalidProcedureCodes'

  @Input()
  public validateCPT = true

  constructor(
    @Optional() @Self() public override ngControl: NgControl,
    @Optional() parentForm: NgForm,
    @Optional() parentFormGroup: FormGroupDirective,
    focusMonitor: FocusMonitor,
    elementRef: ElementRef,
    private readonly changeDetectorRef: ChangeDetectorRef,
  ) {
    super(ngControl, parentForm, parentFormGroup, focusMonitor, elementRef)
  }

  public get value() {
    return this._value || []
  }

  public set value(value: string[]) {
    super.value = (value || []).filter((item, i, self) => self.indexOf(item) === i)
  }

  public readonly separatorKeysCodes = [ENTER, COMMA]

  /** Outcome of the last paste or copy. Cleared when the list changes, unless refused codes are still in the input. */
  public feedback?: { kind: 'success' | 'error', token: string, params?: Record<string, string | number> }

  /** Codes the last paste refused. The control stays invalid until the input is cleared of them. */
  public rejectedCodes: string[] = []

  public get hasRejectedCodes() {
    return this.rejectedCodes.length > 0
  }

  public removeCode(code: string) {
    this.clearFeedback()
    this.value = this.value.filter((c) => c !== code)
  }

  public removeAllCodes() {
    this.clearFeedback()
    this.value = []
  }

  public copyCodes() {
    const copying = navigator.clipboard?.writeText(this.value.join(','))

    if (!copying) {
      // no clipboard API at all, e.g. an insecure origin
      this.setFeedback({ kind: 'error', token: 'common.copy-failed' })
      return
    }

    copying.then(
      () => this.setFeedback({ kind: 'success', token: 'common.copied' }),
      () => this.setFeedback({ kind: 'error', token: 'common.copy-failed' }),
    )
  }

  public addCode(e: MatChipInputEvent) {
    // a pasted list arrives as one token, so split it rather than reject it
    const codes = this.splitCodes(e.value)

    if (!codes.length) {
      return
    }

    const accepted = this.validateCPT ? codes.filter((code) => isValidCPT(code)) : codes
    this.rejectedCodes = codes.filter((code) => !accepted.includes(code))

    const previousCount = this.value.length

    if (accepted.length) {
      this.value = Array.from(
        new Set([...this.value, ...accepted]),
      )
    }

    // only the refused codes stay behind, so the chip input shows exactly what still needs fixing
    e.chipInput?.clear()
    if (this.hasRejectedCodes && e.input) {
      e.input.value = this.rejectedCodes.join(', ')
    }

    this.setFeedback(this.describeOutcome(this.value.length - previousCount, accepted.length))
    this.syncRejectedCodesError()
  }

  /**
   * The refused codes are left in the input for the operator to fix, so typing has to re-read them.
   * Only while something is already refused, or every keystroke of a fresh code would look invalid.
   */
  public onInputChange(e: Event) {
    if (!this.hasRejectedCodes) {
      return
    }

    const codes = this.splitCodes((e.target as HTMLInputElement).value)
    this.rejectedCodes = this.validateCPT ? codes.filter((code) => !isValidCPT(code)) : []

    this.setFeedback(this.hasRejectedCodes ? this.rejectedFeedback() : undefined)
    this.syncRejectedCodesError()
  }

  private splitCodes(value: string) {
    return (value || '')
      .toUpperCase()
      .split(/[\s,]+/)
      .map((code) => code.trim())
      .filter(Boolean)
  }

  /** A single typed code needs no announcement; a paste does, especially when codes were refused or deduped. */
  private describeOutcome(added: number, submitted: number) {
    if (this.hasRejectedCodes) {
      return this.rejectedFeedback()
    }

    if (submitted - added > 0) {
      return {
        kind: 'success' as const,
        token: 'common.codes-added-with-duplicates',
        params: { count: added, duplicates: submitted - added },
      }
    }

    return submitted > 1
      ? { kind: 'success' as const, token: 'common.codes-added', params: { count: added } }
      : undefined
  }

  private rejectedFeedback() {
    return {
      kind: 'error' as const,
      token: 'common.invalid-procedure-codes',
      params: { codes: this.rejectedCodes.join(', ') },
    }
  }

  // removing chips leaves the refused codes in the input, so the refusal stays on screen until the input is fixed
  private clearFeedback() {
    this.setFeedback(this.hasRejectedCodes ? this.rejectedFeedback() : undefined)
  }

  /**
   * Registered on the control rather than stamped with `setErrors`, because anything that
   * revalidates `codes` (the cross-field `requiredIfRelatedFieldIsEmpty`, for one) replaces the
   * errors object and would drop a stamped error while the refused codes are still in the input.
   */
  private readonly rejectedCodesValidator: ValidatorFn = () => (
    this.hasRejectedCodes ? { [CodesSelectorInputComponent.INVALID_CODES_ERROR]: this.rejectedCodes } : null
  )

  private syncRejectedCodesError() {
    const control = this.ngControl?.control

    if (control) {
      if (!control.hasValidator(this.rejectedCodesValidator)) {
        control.addValidators(this.rejectedCodesValidator)
      }

      control.updateValueAndValidity({ emitEvent: false })
    }

    this.updateErrorState()
  }

  // the clipboard promise settles outside the dialog's OnPush check, so the message needs a nudge
  private setFeedback(feedback?: CodesSelectorInputComponent['feedback']) {
    this.feedback = feedback
    this.changeDetectorRef.markForCheck()
  }
}
